// hooks/useRoles.ts
import { useState } from 'react';
import { Role } from '../types/roles';
import dataProvider from '../../services/api';

export const useRoles = (
  showSuccessMessage: (message: string) => void,
  handleErrorWithLogout: (error: any) => void
) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');

  const fetchRoles = async () => {
    try {
      const { data: rolesData } = await dataProvider.getAllRoles('roles');
      setRoles(rolesData);

      if (rolesData.length > 0) {
        const updatedSelectedRole = selectedRole
          ? rolesData.find((role: Role) => role.id === selectedRole.id)
          : null;

        if (updatedSelectedRole) {
          setSelectedRole(updatedSelectedRole);
          setRoleName(updatedSelectedRole.name);
          setRoleDescription(updatedSelectedRole.rights[0]?.description || '');
        } else {
          const newRole = rolesData[0];
          setSelectedRole(newRole);
          setRoleName(newRole.name);
          setRoleDescription(newRole.rights[0]?.description || '');
        }
      } else {
        setSelectedRole(null);
        setRoleName('');
        setRoleDescription('');
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setRoles([]);
      setSelectedRole(null);
      setRoleName('');
      setRoleDescription('');
    }
  };

  const createRole = async (name: string, description: string) => {
    try {
      const { data } = await dataProvider.createRole('administration', {
        name,
        description,
      });

      const newRole: Role = {
        id: data.id,
        name: data.name,
        description: data.description,
      };

      await fetchRoles();
      setSelectedRole(newRole);
      setRoleName(newRole.name);
      setRoleDescription(newRole.description);
      showSuccessMessage('Role created successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
      await fetchRoles();
    }
  };

  const updateRole = async () => {
    if (!selectedRole) return;

    try {
      const { data } = await dataProvider.updateRole('roles', {
        id: selectedRole.id,
        name: roleName,
        description: roleDescription,
      });

      await fetchRoles();
      const updatedRole = { ...selectedRole, name: data.name, description: data.rights[0]?.description || '' };
      setSelectedRole(updatedRole);
      setRoleName(updatedRole.name);
      setRoleDescription(updatedRole.description);
      showSuccessMessage('Role updated successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const deleteRole = async () => {
    if (!selectedRole) return;

    try {
      await dataProvider.deleteRole('roles', { id: selectedRole.id });
      await fetchRoles();
      showSuccessMessage('Role deleted successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const selectRole = (role: Role) => {
    setSelectedRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description);
  };

  return {
    roles,
    setRoles,
    selectedRole,
    setSelectedRole,
    roleName,
    setRoleName,
    roleDescription,
    setRoleDescription,
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    selectRole,
  };
};